import React from "react";
import RIcon from "../../reservas/components/ReservasIcons";
import { SucursalCurrent, SucursalPicker } from "../../reservas/components/Sucursales";
import { cartTotals, formatDuration, formatMoney, lineasDeCarrito } from "../beautyBooking";
import { ONLINE_BOOKING_DISABLED_MESSAGE } from "../../reservas/onlineBooking";
import { claveDeLinea, esAdicional, nombreDeLinea, tieneOpciones } from "../../../helpers/beautyLineas";

/**
 * Desktop sticky "Reservá tu turno" aside, using the same `reserve-stick`
 * markup as Canchas. Presentational: the cart and its handlers come from the
 * booking flow; with no cart it shows the empty text and a disabled CTA.
 */
const BookingAside = ({
  salonName,
  cart = [],
  choices = {},
  policyLines = [],
  onRemove,
  onEdit,
  onStartBooking,
  sucursales = [],
  casaCentral = null,
  sedes = null,
  bookingDisabled = false,
}) => {
  // One row per appointment line: the service (with its variant) and, under it, its add-ons.
  const lines = lineasDeCarrito(cart, choices);
  const totals = cartTotals(lines);
  const empty = cart.length === 0;
  return (
    <aside className="bd-sidebar-sticky" aria-label="Hacer una reserva">
      <div className="reserve-stick">
        <div className="rs-head">
          <div className="t">Hacer una reserva</div>
          {salonName && <div className="n">{salonName}</div>}
        </div>
        <div className="rs-body">
          <SucursalPicker kind="beauty" currentName={salonName} sucursales={sucursales} sedes={sedes} />
          <SucursalCurrent kind="beauty" name={salonName} casaCentral={casaCentral} />
          {empty ? (
            <div className="rs-empty">
              <RIcon name="spark" width={34} height={34} />
              <br />
              Todavía no agregaste servicios. Elegí uno o más de la lista.
            </div>
          ) : (
            <>
              {lines.map((line, index) => {
                const service = cart.find((s) => String(s._id) === line.serviceId);
                const addOn = esAdicional(line);
                return (
                  <div key={claveDeLinea(line, index)} className={`rs-row beauty-cart-item${addOn ? " beauty-cart-addon" : ""}`}>
                    <span className="k beauty-cart-item-name">
                      {nombreDeLinea(line)}
                      <small>{formatDuration(line.durationMinutes)}</small>
                    </span>
                    <span className="v">{formatMoney(line.price)}</span>
                    {!addOn && onEdit && tieneOpciones(service) && (
                      <button type="button" className="beauty-link-btn" aria-label={`Editar opciones de ${line.name}`} onClick={() => onEdit(service)}>
                        Editar
                      </button>
                    )}
                    {!addOn && onRemove && (
                      <button
                        type="button"
                        className="beauty-remove-btn"
                        aria-label={`Quitar ${line.name}`}
                        onClick={() => onRemove(service)}
                      >
                        <RIcon name="x" width={12} height={12} />
                      </button>
                    )}
                  </div>
                );
              })}
              <div className="rs-div" />
              <div className="rs-row">
                <span className="k"><RIcon name="clock" />Duración</span>
                <span className="v">{formatDuration(totals.durationMinutes)}</span>
              </div>
              <div className="rs-row">
                <span className="k"><RIcon name="ticket" />Seña</span>
                <span className="v">{totals.deposit > 0 ? formatMoney(totals.deposit) : "Sin seña"}</span>
              </div>
              <div className="rs-div" />
              <div className="rs-total">
                <span className="k">Total</span>
                <span className="v">{formatMoney(totals.price)}</span>
              </div>
            </>
          )}
          <button
            type="button"
            className="r-btn-primary rs-cta beauty-aside-cta"
            disabled={empty || bookingDisabled}
            aria-disabled={empty || bookingDisabled}
            onClick={() => onStartBooking?.()}
          >
            {empty ? "Elegí al menos un servicio" : <>Ver disponibilidad <RIcon name="arrowR" width={17} height={17} /></>}
          </button>
          {bookingDisabled && (
            <div className="rs-note" role="note" style={{ margin: "10px 0 0", color: "var(--zoco-gray-2)" }}>
              <RIcon name="info" width={15} height={15} />
              {ONLINE_BOOKING_DISABLED_MESSAGE}
            </div>
          )}
        </div>
      </div>

      {policyLines.length > 0 && (
        <div className="policy-box">
          {policyLines.map((line, index) => (
            <div className="policy" key={line}>
              <RIcon name={index === 0 ? "cal" : "shield"} width={16} height={16} />
              {line}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
};

export default BookingAside;
